import { useLanguage } from "@/contexts/LanguageContext";
import { useStatusBarMode } from "./hooks/useStatusBarMode";
import { Sparkles, Wifi, WifiOff, FlaskConical, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Persistent, slim status bar shown above page content.
 * 4 visible modes: sample | demo | real | disconnected (hidden for non-traders).
 *
 * The tour-progress chip lives under the TourGuideButton in the TopBar
 * (see TourResumeChip), not here, to keep this bar focused on account state.
 */
export const TourStatusBar = () => {
  const { language } = useLanguage();
  const { mode } = useStatusBarMode();
  const fa = language === "fa";

  if (mode === "hidden") return null;

  const config = {
    sample: {
      icon: FlaskConical,
      message: fa
        ? "حالت داده‌ی نمونه فعال • این داده‌ها صرفاً برای آشنایی هستند و ماهیت واقعی ندارند"
        : "Sample Data mode • For exploration only — not real trading data",
      classes:
        "bg-amber-500/10 text-amber-900 dark:text-amber-200 border-amber-500/30",
      dot: "bg-amber-500",
    },
    demo: {
      icon: Sparkles,
      message: fa
        ? "اکانت دموی بروکر متصل است • داده‌ی بازار واقعی، بدون پول واقعی"
        : "Connected to a broker Demo account • Real market, no real money",
      classes: "bg-sky-500/10 text-sky-900 dark:text-sky-200 border-sky-500/30",
      dot: "bg-sky-500",
    },
    real: {
      icon: Wifi,
      message: fa
        ? "اکانت لایو متصل است • معاملات واقعی در حال اجرا"
        : "Live account connected • Real trading in progress",
      classes:
        "bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 border-emerald-500/30",
      dot: "bg-emerald-500 animate-pulse",
    },
    disconnected: {
      icon: WifiOff,
      message: fa
        ? "ارتباط با بروکر قطع شده • EA رو چک کن یا اتصال رو دوباره برقرار کن"
        : "Broker connection lost • Check the EA or reconnect",
      classes: "bg-red-500/10 text-red-900 dark:text-red-200 border-red-500/30",
      dot: "bg-red-500",
    },
  } as const;

  const cfg = config[mode];
  const Icon = cfg.icon;

  return (
    <div
      className={cn(
        "w-full border-b text-xs flex items-center justify-center gap-2 py-1.5 px-3",
        cfg.classes,
      )}
      role="status"
      aria-live="polite"
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", cfg.dot)} />
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
      <span className="truncate font-medium">{cfg.message}</span>
      <Clock className="h-3 w-3 shrink-0 opacity-50 ml-2" />
    </div>
  );
};

